import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Package,
  Store,
  Gauge,
  RotateCcw,
  Unplug,
  PackagePlus,
  ShieldCheck,
  Wrench,
  Star,
  Plus,
  Trash2,
  Loader2,
  ClipboardList,
  Sparkles,
  ChevronDown,
  BadgeCheck,
  MessageSquareText,
  GripVertical,
  Settings,
  Send,
  Calendar,
  Check,
  Mail,
  Palette,
} from 'lucide-react';
import { BoardSearchSimplePickerPanel } from '../../board/board-search-filters/BoardSearchSimplePickerPanel';
import { computeToolbarFilterPopoverLayout } from '../../board/board-search-filters/toolbarFilterPopoverLayout';
import {
  PeriodModal,
  getCalendarBounds,
} from '../../equipment-consult/components/EquipmentConsultProductionPeriodPicker';
import '../../board/board-search-filters/BoardSearchFilters.css';
import '../../equipment-consult/components/EquipmentConsultProductionPeriodPicker.css';
import '../../process-config/ConfigStatusView2.css';
import ProcessConfigSkeleton from '../../process-config/ConfigStatusView2Skeleton';
import './CustomerExperienceView.css';
import { useCxExpToast } from './CxExpSonnerToast';
import CxExpAnchorTooltip from './CxExpAnchorTooltip';
import CxEmailTemplateModal from './CxEmailTemplateModal';
import { isDefaultCxEmailTemplate, mergeCxEmailTemplate } from '../utils/cxEmailTemplateDefaults';

const API = '/api/vc-usr/cx/config';

const OS_TOGGLES = [
  { key: 'os_stock', label: 'Estoque', Icon: Package },
  { key: 'os_counter', label: 'Balcão', Icon: Store },
  { key: 'os_meter', label: 'Aferição', Icon: Gauge },
  { key: 'os_return', label: 'Retorno/Recarga', Icon: RotateCcw },
  { key: 'os_uninstall', label: 'Desinstalação', Icon: Unplug },
  { key: 'os_install', label: 'Instalação', Icon: PackagePlus },
  { key: 'os_prevent', label: 'Preventiva', Icon: ShieldCheck },
  { key: 'os_correct', label: 'Corretiva', Icon: Wrench },
];

const SECTIONS = [
  { id: 'os', label: 'Configurações gerais', Icon: Settings },
  { id: 'rules', label: 'Regras de avaliação', Icon: Sparkles },
];

const STAR_OPTIONS = [1, 2, 3, 4, 5];
const STAR_PICKER_WIDTH = 120;
const STAR_PICKER_MAX_HEIGHT = 220;
const AUTO_ARCHIVE_POPOVER_WIDTH = 248;
const AUTO_ARCHIVE_POPOVER_MAX_HEIGHT = 168;

function authHeaders() {
  const token = localStorage.getItem('authToken');
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

function emptyOsTypes() {
  return Object.fromEntries(OS_TOGGLES.map((o) => [o.key, false]));
}

function newRule(partial = {}) {
  return {
    localId: `rule_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    isDraft: true,
    starsMin: null,
    starsMax: null,
    askReason: false,
    requireNote: false,
    requireNoteOnMotive: false,
    motives: [],
    ...partial,
  };
}

function starsLabel(min, max) {
  if (min == null || max == null) return 'Faixa não definida';
  if (min === max) return `${min} estrela${min > 1 ? 's' : ''}`;
  return `${min} a ${max} estrelas`;
}

function ruleIsComplete(rule) {
  if (rule.starsMin == null || rule.starsMax == null) return false;
  if (rule.askReason && !rule.motives.length) return false;
  return true;
}

function ruleDeleteLabel(rule) {
  if (rule.starsMin != null && rule.starsMax != null) return starsLabel(rule.starsMin, rule.starsMax);
  return 'esta regra';
}

function normalizeRuleSnapshot(rule) {
  return {
    starsMin: rule.starsMin,
    starsMax: rule.starsMax,
    askReason: Boolean(rule.askReason),
    requireNote: Boolean(rule.requireNote),
    requireNoteOnMotive: Boolean(rule.requireNoteOnMotive),
    motives: (rule.motives || []).map((m) => ({
      label: m.label,
      requireDetail: Boolean(m.requireDetail),
    })),
  };
}

function snapshotConfig(
  osTypes,
  rules,
  ratingSendMgmt,
  ratingSendMgmtDate,
  ratingAutoArchive,
  ratingAutoArchiveDays
) {
  return JSON.stringify({
    osTypes,
    ratingSendMgmt: Boolean(ratingSendMgmt),
    ratingSendMgmtDate: ratingSendMgmtDate || null,
    ratingAutoArchive: Boolean(ratingAutoArchive),
    ratingAutoArchiveDays:
      ratingAutoArchive && Number(ratingAutoArchiveDays) > 0 ? Number(ratingAutoArchiveDays) : null,
    rules: rules.map(normalizeRuleSnapshot),
  });
}

function formatSendMgmtDateLabel(isoDate) {
  if (!isoDate) return '';
  const [y, m, d] = isoDate.split('-');
  if (!y || !m || !d) return isoDate;
  return `${d}/${m}/${y}`;
}

function mapRulesFromApi(list) {
  return (list || []).map((r) => ({
    localId: `rule_${r.id}`,
    id: r.id,
    starsMin: r.starsMin,
    starsMax: r.starsMax,
    askReason: Boolean(r.askReason),
    requireNote: Boolean(r.requireNote),
    requireNoteOnMotive: Boolean(r.requireNoteOnMotive),
    motives: (r.motives || []).map((m) => ({
      id: m.id,
      label: m.label,
      requireDetail: Boolean(m.requireDetail),
    })),
  }));
}

function rulesStarOverlap(a, b) {
  if (a.starsMin == null || a.starsMax == null || b.starsMin == null || b.starsMax == null) return false;
  return a.starsMin <= b.starsMax && b.starsMin <= a.starsMax;
}

function findStarRangeConflict(rules, localId) {
  const rule = rules.find((r) => r.localId === localId);
  if (!rule || rule.starsMin == null || rule.starsMax == null) return null;
  return rules.find((r) => r.localId !== localId && rulesStarOverlap(rule, r)) || null;
}

function findAnyStarRangeConflict(rules) {
  for (let i = 0; i < rules.length; i += 1) {
    for (let j = i + 1; j < rules.length; j += 1) {
      if (rulesStarOverlap(rules[i], rules[j])) return { a: rules[i], b: rules[j] };
    }
  }
  return null;
}

function starRangeConflictMessage(a, b) {
  return `Faixa em conflito: ${starsLabel(a.starsMin, a.starsMax)} sobrepõe ${starsLabel(b.starsMin, b.starsMax)}.`;
}

function CxPcs2StatusChip({ active, label }) {
  return (
    <span className={`cx-exp-pcs2-status${active ? ' cx-exp-pcs2-status--on' : ' cx-exp-pcs2-status--off'}`}>
      {label}
    </span>
  );
}

function CxPcs2Section({ icon: Icon, title, desc, status, children, stacked = false }) {
  if (stacked) {
    return (
      <section className="pcs2-section pcs2-section--stacked">
        <div className="pcs2-section-top">
          <div className="pcs2-section-heading">
            <Icon size={16} strokeWidth={1.75} aria-hidden />
            <h3 className="pcs2-section-title">{title}</h3>
            {status || null}
          </div>
        </div>
        <div className="pcs2-section-body pcs2-section-body--full">{children}</div>
      </section>
    );
  }

  return (
    <section className="pcs2-section">
      <div className="pcs2-section-aside">
        <div className="pcs2-section-heading">
          <Icon size={16} strokeWidth={1.75} aria-hidden />
          <h3 className="pcs2-section-title">{title}</h3>
          {status || null}
        </div>
        {desc ? <p className="pcs2-section-desc">{desc}</p> : null}
      </div>
      <div className="pcs2-section-body">{children}</div>
    </section>
  );
}

function CxPcs2TooltipHost({ text, children, align = 'start' }) {
  if (!text) return children;
  return (
    <span
      className={[
        'pcs2-tooltip-host',
        align === 'end' ? 'pcs2-tooltip-host--align-end' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      data-tooltip={text}
    >
      {children}
    </span>
  );
}

function CxPcs2SettingRow({ title, desc, children }) {
  return (
    <div className="pcs2-setting-row">
      <div className="pcs2-setting-copy">
        <span className="pcs2-setting-title">{title}</span>
        {desc ? <span className="pcs2-setting-desc">{desc}</span> : null}
      </div>
      <div className="pcs2-setting-actions">{children}</div>
    </div>
  );
}

function CxPcs2Switch({ checked, onChange, ariaLabel }) {
  const inputRef = useRef(null);
  const handleChange = (e) => {
    onChange(e);
    requestAnimationFrame(() => inputRef.current?.blur());
  };
  return (
    <label className="pcs2-switch">
      <input
        ref={inputRef}
        type="checkbox"
        checked={checked}
        onChange={handleChange}
        aria-label={ariaLabel}
      />
      <span className="pcs2-switch-pill">
        <span className="pcs2-switch-dot" />
      </span>
    </label>
  );
}

function CxSendMgmtDatePicker({ value, onChange, disabled }) {
  const btnRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [anchorRect, setAnchorRect] = useState(null);
  const configured = Boolean(value);
  const bounds = getCalendarBounds();

  const openPicker = () => {
    if (disabled) return;
    const rect = btnRef.current?.getBoundingClientRect() || null;
    setAnchorRect(rect);
    setOpen((v) => !v);
  };

  return (
    <span className="cx-exp-send-date">
      <CxExpAnchorTooltip
        text="Determine o período inicial para gestão das avaliações"
        align="end"
      >
        <button
          ref={btnRef}
          type="button"
          className="cx-exp-send-date-icon"
          disabled={disabled}
          aria-label="Definir período inicial de gestão"
          aria-expanded={open}
          onClick={openPicker}
        >
          <Calendar size={15} strokeWidth={1.75} aria-hidden />
        </button>
      </CxExpAnchorTooltip>
      {configured ? (
        <CxExpAnchorTooltip
          text={`Período inicial configurado (${formatSendMgmtDateLabel(value)})`}
          align="end"
          compact
        >
          <span className="cx-exp-send-date-check" aria-label="Período inicial configurado">
            <Check size={14} strokeWidth={2.25} aria-hidden />
          </span>
        </CxExpAnchorTooltip>
      ) : null}
      {open ? (
        <PeriodModal
          anchorRef={btnRef}
          anchorRect={anchorRect}
          currentPeriod={value ? { startDate: value, endDate: value } : null}
          minDate={bounds.minDate}
          maxDate={bounds.maxDate}
          dayMode
          singleMonth
          hideHeader
          hideActionsBorder
          useUserFormActions
          modalClassName="ec-module-popover cx-exp-send-date-period-modal"
          zIndex={10020}
          title="Período inicial"
          onApply={(startDate) => {
            onChange(startDate || '');
          }}
          onClear={() => {
            onChange('');
          }}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </span>
  );
}

function CxAutoArchiveDaysConfig({ value, onChange, disabled }) {
  const anchorRef = useRef(null);
  const inputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [layout, setLayout] = useState(null);
  const configured = Number(value) > 0;

  const updateLayout = useCallback(() => {
    const el = anchorRef.current;
    if (!el) {
      setLayout(null);
      return;
    }
    const rect = el.getBoundingClientRect();
    setLayout(
      computeToolbarFilterPopoverLayout(rect, AUTO_ARCHIVE_POPOVER_WIDTH, AUTO_ARCHIVE_POPOVER_MAX_HEIGHT)
    );
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setLayout(null);
      return;
    }
    updateLayout();
  }, [open, updateLayout]);

  useEffect(() => {
    if (!open) return undefined;
    const onResize = () => updateLayout();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
    };
  }, [open, updateLayout]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      const t = event.target;
      if (anchorRef.current?.contains(t)) return;
      if (t.closest?.('.board-search-filters-popover')) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setDraft(configured ? String(value) : '');
    const t = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [open, configured, value]);

  const applyDays = () => {
    const n = parseInt(String(draft).trim(), 10);
    if (!Number.isFinite(n) || n < 1) return;
    onChange(n);
    setOpen(false);
  };

  const openPopover = () => {
    if (disabled) return;
    setOpen((v) => !v);
  };

  return (
    <>
      <span className="cx-exp-auto-archive-config" ref={anchorRef}>
        <CxPcs2TooltipHost text="Configurar dias para arquivamento automático" align="end">
          <button
            type="button"
            className="pcs2-btn-gear"
            disabled={disabled}
            aria-label="Configurar dias para arquivamento automático"
            aria-expanded={open}
            onClick={openPopover}
          >
            <Settings size={15} strokeWidth={1.75} />
          </button>
        </CxPcs2TooltipHost>
        {configured ? (
          <CxExpAnchorTooltip
            text={`Arquivar após ${value} dia${value > 1 ? 's' : ''}`}
            align="end"
            compact
          >
            <span className="cx-exp-send-date-check" aria-label="Dias configurados">
              <Check size={14} strokeWidth={2.25} aria-hidden />
            </span>
          </CxExpAnchorTooltip>
        ) : null}
      </span>
      {open && layout
        ? createPortal(
            <div
              className="board-search-filters-popover board-search-filters-popover--simple cx-exp-auto-archive-popover"
              style={{
                position: layout.position,
                top: layout.top,
                left: layout.left,
                width: layout.width,
                boxSizing: layout.boxSizing,
                zIndex: layout.zIndex,
                maxHeight: layout.maxHeight,
                '--bsf-cidade-popover-max-h': layout['--bsf-cidade-popover-max-h'],
                transform: layout.flipped ? 'translateY(-100%)' : undefined,
              }}
              role="dialog"
              aria-label="Dias para arquivamento automático"
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <label className="cx-exp-auto-archive-popover-label" htmlFor="cx-exp-auto-archive-days">
                Arquivar após (dias)
              </label>
              <input
                ref={inputRef}
                id="cx-exp-auto-archive-days"
                type="number"
                min={1}
                step={1}
                className="cx-exp-auto-archive-popover-input"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    applyDays();
                  }
                  if (e.key === 'Escape') {
                    e.preventDefault();
                    setOpen(false);
                  }
                }}
              />
              <div className="cx-exp-auto-archive-popover-actions">
                <button type="button" className="cx-exp-btn cx-exp-btn--ghost" onClick={() => setOpen(false)}>
                  Cancelar
                </button>
                <button
                  type="button"
                  className="cx-exp-btn cx-exp-btn--primary"
                  disabled={!Number.isFinite(parseInt(String(draft).trim(), 10)) || parseInt(String(draft).trim(), 10) < 1}
                  onClick={applyDays}
                >
                  Aplicar
                </button>
              </div>
            </div>,
            document.body
          )
        : null}
    </>
  );
}

function StarRow({ min, max }) {
  if (min == null || max == null) return null;
  return (
    <span className="cx-exp-stars" aria-hidden>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          size={12}
          strokeWidth={2}
          className={n >= min && n <= max ? 'cx-exp-stars__on' : 'cx-exp-stars__off'}
          fill={n >= min && n <= max ? 'currentColor' : 'none'}
        />
      ))}
    </span>
  );
}

export default function CustomerExperienceView({ embedded = false }) {
  const [section, setSection] = useState('os');
  const [osTypes, setOsTypes] = useState(emptyOsTypes);
  const [ratingSendMgmt, setRatingSendMgmt] = useState(false);
  const [ratingSendMgmtDate, setRatingSendMgmtDate] = useState('');
  const [ratingAutoArchive, setRatingAutoArchive] = useState(false);
  const [ratingAutoArchiveDays, setRatingAutoArchiveDays] = useState(null);
  const [emailTemplate, setEmailTemplate] = useState(() => mergeCxEmailTemplate(null));
  const [emailTemplateOpen, setEmailTemplateOpen] = useState(false);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { success: toastSuccess, error: toastError, Toaster } = useCxExpToast();
  const [ruleDeleteTarget, setRuleDeleteTarget] = useState(null);
  const [deletingRule, setDeletingRule] = useState(false);
  const [savedSnapshot, setSavedSnapshot] = useState('');

  const applyConfig = useCallback((data) => {
    const os = { ...emptyOsTypes(), ...(data.osTypes || {}) };
    const rls = mapRulesFromApi(data.rules);
    const sendMgmt = Boolean(data.ratingSendMgmt);
    const sendMgmtDate = data.ratingSendMgmtDate || '';
    const autoArchive = Boolean(data.ratingAutoArchive);
    const autoArchiveDays =
      data.ratingAutoArchiveDays != null && Number(data.ratingAutoArchiveDays) > 0
        ? Number(data.ratingAutoArchiveDays)
        : null;
    const template = mergeCxEmailTemplate(data.emailTemplate);
    setOsTypes(os);
    setRatingSendMgmt(sendMgmt);
    setRatingSendMgmtDate(sendMgmtDate);
    setRatingAutoArchive(autoArchive);
    setRatingAutoArchiveDays(autoArchiveDays);
    setEmailTemplate(template);
    setRules(rls);
    setSavedSnapshot(
      snapshotConfig(os, rls, sendMgmt, sendMgmtDate, autoArchive, autoArchiveDays)
    );
    return { os, rls, sendMgmt, sendMgmtDate, autoArchive, autoArchiveDays, template };
  }, []);

  const isDirty = useMemo(
    () =>
      savedSnapshot !==
      snapshotConfig(
        osTypes,
        rules,
        ratingSendMgmt,
        ratingSendMgmtDate,
        ratingAutoArchive,
        ratingAutoArchiveDays
      ),
    [
      savedSnapshot,
      osTypes,
      rules,
      ratingSendMgmt,
      ratingSendMgmtDate,
      ratingAutoArchive,
      ratingAutoArchiveDays,
    ]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(API, { headers: authHeaders() });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json.success === false) throw new Error(json.message || `Erro ${res.status}`);
      applyConfig(json.data || {});
    } catch (e) {
      toastError(e.message || 'Falha ao carregar');
    } finally {
      setLoading(false);
    }
  }, [applyConfig, toastError]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleOs = (key) => {
    setOsTypes((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const hasDraftRule = useMemo(() => rules.some((r) => r.isDraft), [rules]);

  const addRule = () => {
    if (hasDraftRule) return;
    setRules((prev) => [...prev, newRule()]);
    setSection('rules');
  };

  const updateRule = (localId, patch) => {
    setRules((prev) => {
      const next = prev.map((r) => (r.localId === localId ? { ...r, ...patch } : r));
      const conflict = findStarRangeConflict(next, localId);
      if (conflict) {
        const cur = next.find((r) => r.localId === localId);
        toastError(starRangeConflictMessage(cur, conflict));
        return prev;
      }
      return next;
    });
  };

  const removeRule = (localId) => {
    setRules((prev) => prev.filter((r) => r.localId !== localId));
  };

  const requestRemoveRule = (rule) => {
    setRuleDeleteTarget({
      localId: rule.localId,
      id: rule.id,
      label: ruleDeleteLabel(rule),
    });
  };

  const confirmRemoveRule = async () => {
    if (!ruleDeleteTarget || deletingRule) return;
    const { localId, id } = ruleDeleteTarget;
    setDeletingRule(true);
    try {
      if (id) {
        const res = await fetch(`${API}/rules/${id}`, {
          method: 'DELETE',
          headers: authHeaders(),
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok || json.success === false) throw new Error(json.message || `Erro ${res.status}`);
      }
      const nextRules = rules.filter((r) => r.localId !== localId);
      setRules(nextRules);
      setSavedSnapshot(
        snapshotConfig(
          osTypes,
          nextRules,
          ratingSendMgmt,
          ratingSendMgmtDate,
          ratingAutoArchive,
          ratingAutoArchiveDays
        )
      );
      setRuleDeleteTarget(null);
      if (id) toastSuccess('Regra excluída.');
    } catch (e) {
      toastError(e.message || 'Erro ao excluir regra');
    } finally {
      setDeletingRule(false);
    }
  };

  const addMotive = (localId, label, requireDetail = false) => {
    const txt = String(label || '').trim();
    if (!txt) return;
    setRules((prev) =>
      prev.map((r) => {
        if (r.localId !== localId) return r;
        if (r.motives.some((m) => m.label.toLowerCase() === txt.toLowerCase())) return r;
        return {
          ...r,
          motives: [...r.motives, { label: txt, requireDetail: Boolean(requireDetail) }],
        };
      })
    );
  };

  const toggleMotiveDetail = (localId, idx) => {
    setRules((prev) =>
      prev.map((r) => {
        if (r.localId !== localId) return r;
        return {
          ...r,
          motives: r.motives.map((m, i) =>
            i === idx ? { ...m, requireDetail: !m.requireDetail } : m
          ),
        };
      })
    );
  };

  const removeMotive = (localId, idx) => {
    setRules((prev) =>
      prev.map((r) =>
        r.localId === localId ? { ...r, motives: r.motives.filter((_, i) => i !== idx) } : r
      )
    );
  };

  const reorderMotive = (localId, fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;
    setRules((prev) =>
      prev.map((r) => {
        if (r.localId !== localId) return r;
        const next = [...r.motives];
        const [removed] = next.splice(fromIndex, 1);
        next.splice(toIndex, 0, removed);
        return { ...r, motives: next };
      })
    );
  };

  const save = async () => {
    const incomplete = rules.find((r) => !ruleIsComplete(r));
    if (incomplete) {
      if (incomplete.starsMin == null || incomplete.starsMax == null) {
        toastError('Preencha a faixa de estrelas de todas as regras antes de salvar.');
      } else {
        toastError('Informe ao menos um motivo nas regras com "Solicitar motivo" ativo.');
      }
      return;
    }

    const overlap = findAnyStarRangeConflict(rules);
    if (overlap) {
      toastError(starRangeConflictMessage(overlap.a, overlap.b));
      return;
    }

    if (ratingAutoArchive && (!ratingAutoArchiveDays || Number(ratingAutoArchiveDays) < 1)) {
      toastError('Defina quantidade de dias maior que zero para arquivamento automático.');
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(API, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({
          osTypes,
          ratingSendMgmt,
          ratingSendMgmtDate: ratingSendMgmtDate || null,
          ratingAutoArchive,
          ratingAutoArchiveDays: ratingAutoArchive ? ratingAutoArchiveDays : null,
          rules: rules.map((r) => ({
            starsMin: r.starsMin,
            starsMax: r.starsMax,
            askReason: r.askReason,
            requireNote: r.requireNote,
            requireNoteOnMotive: r.requireNoteOnMotive,
            motives: r.motives.map((m) => ({
              label: m.label,
              requireDetail: Boolean(m.requireDetail),
            })),
          })),
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json.success === false) throw new Error(json.message || `Erro ${res.status}`);
      applyConfig(json.data || {});
      toastSuccess('Configuração salva.');
    } catch (e) {
      toastError(e.message || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const osTypesEnabledCount = useMemo(
    () => OS_TOGGLES.filter((o) => osTypes[o.key]).length,
    [osTypes]
  );
  const rulesStatusLabel = useMemo(() => {
    const n = rules.length;
    if (n === 0) return 'Nenhuma';
    if (n === 1) return '1 regra';
    return `${n} regras`;
  }, [rules.length]);

  const emailTemplateStatusLabel = useMemo(
    () => (isDefaultCxEmailTemplate(emailTemplate) ? 'Padrão' : 'Personalizado'),
    [emailTemplate]
  );
  if (loading) {
    return (
      <div className={`cx-exp-page${embedded ? ' cx-exp-page--embedded' : ''}`}>
        <ProcessConfigSkeleton variant="settings" ariaLabel="Carregando experiência do cliente" />
        {Toaster}
      </div>
    );
  }

  return (
    <div className={`cx-exp-page${embedded ? ' cx-exp-page--embedded' : ''}`}>
      <div className="cx-exp-shell">
        <aside className="cx-exp-sidebar">
          <div className="cx-exp-sidebar-head">
            <h1 className="cx-exp-sidebar-title">Experiência do cliente</h1>
            <p className="cx-exp-sidebar-sub">Análise e gestão das avaliações de atendimento</p>
          </div>
          <nav className="cx-exp-nav">
            {SECTIONS.map((s) => {
              const Icon = s.Icon;
              return (
                <button
                  key={s.id}
                  type="button"
                  className={`cx-exp-nav-item${section === s.id ? ' active' : ''}`}
                  onClick={() => setSection(s.id)}
                >
                  <Icon size={15} strokeWidth={2} aria-hidden />
                  <span>{s.label}</span>
                </button>
              );
            })}
          </nav>
        </aside>

        <div className="cx-exp-main">
          <div className="cx-exp-main-content">
            {section === 'os' ? (
              <div className="pcs2-page cx-exp-pcs2-page">
                <CxPcs2Section
                  icon={ClipboardList}
                  title="Tipos de ordem de serviço"
                  desc="Defina quais tipos de ordem de serviço serão avaliados"
                  status={(
                    <CxPcs2StatusChip
                      active={osTypesEnabledCount > 0}
                      label={
                        osTypesEnabledCount === 0
                          ? 'Nenhum'
                          : osTypesEnabledCount === OS_TOGGLES.length
                            ? 'Todos ativos'
                            : `${osTypesEnabledCount} de ${OS_TOGGLES.length} ativos`
                      }
                    />
                  )}
                >
                  <div className="user-form-toggle-wrap cx-exp-os-toggles" role="group" aria-label="Tipos de ordem de serviço avaliáveis">
                    {OS_TOGGLES.map((opt) => {
                      const active = !!osTypes[opt.key];
                      const OsIcon = opt.Icon;
                      return (
                        <button
                          key={opt.key}
                          type="button"
                          aria-pressed={active}
                          className={`user-form-toggle-pill${active ? ' active' : ''}`}
                          onClick={() => toggleOs(opt.key)}
                        >
                          <OsIcon size={14} strokeWidth={2} aria-hidden />
                          <span>{opt.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </CxPcs2Section>

                <hr className="pcs2-divider" />

                <CxPcs2Section
                  icon={Send}
                  title="Controle de envio de avaliações"
                  desc="Habilite a gestão e controle de avaliações internamente"
                  status={(
                    <CxPcs2StatusChip
                      active={ratingSendMgmt}
                      label={
                        ratingSendMgmt
                          ? ratingSendMgmtDate
                            ? `Habilitado · ${formatSendMgmtDateLabel(ratingSendMgmtDate)}`
                            : 'Habilitado'
                          : 'Desabilitado'
                      }
                    />
                  )}
                >
                  <div className="pcs2-card pcs2-card--flat">
                    <CxPcs2SettingRow
                      title="Habilitar gestão de envio de avaliações"
                      desc="Ao habilitar, será criado uma área para gerir o que o cliente irá avaliar"
                    >
                      <div className="cx-exp-send-mgmt-actions">
                        {ratingSendMgmt ? (
                          <CxSendMgmtDatePicker
                            value={ratingSendMgmtDate}
                            onChange={setRatingSendMgmtDate}
                          />
                        ) : null}
                        <CxPcs2Switch
                          checked={ratingSendMgmt}
                          onChange={(e) => setRatingSendMgmt(e.target.checked)}
                          ariaLabel="Habilitar gestão de envio de avaliações"
                        />
                      </div>
                    </CxPcs2SettingRow>
                    <CxPcs2SettingRow
                      title="Arquivar avaliações automaticamente"
                      desc="Ao habilitar, defina em quantos dias a avaliação irá arquivar automaticamente"
                    >
                      <div className="cx-exp-send-mgmt-actions">
                        {ratingAutoArchive ? (
                          <CxAutoArchiveDaysConfig
                            value={ratingAutoArchiveDays}
                            onChange={setRatingAutoArchiveDays}
                          />
                        ) : null}
                        <CxPcs2Switch
                          checked={ratingAutoArchive}
                          onChange={(e) => setRatingAutoArchive(e.target.checked)}
                          ariaLabel="Arquivar avaliações automaticamente"
                        />
                      </div>
                    </CxPcs2SettingRow>
                  </div>
                </CxPcs2Section>

                <hr className="pcs2-divider" />

                <CxPcs2Section
                  icon={Mail}
                  title="Modelo de email"
                  desc="Personalize o modelo de email a ser enviado para avaliação de qualidade"
                  status={(
                    <CxPcs2StatusChip
                      active={!isDefaultCxEmailTemplate(emailTemplate)}
                      label={emailTemplateStatusLabel}
                    />
                  )}
                >
                  <div className="pcs2-card pcs2-card--flat">
                    <CxPcs2SettingRow
                      title="Template de avaliação"
                      desc="Visual, conteúdo e pré-visualização do e-mail enviado ao cliente"
                    >
                      <button
                        type="button"
                        className="cx-exp-email-customize-btn"
                        onClick={() => setEmailTemplateOpen(true)}
                      >
                        <Palette size={14} strokeWidth={2} aria-hidden />
                        Personalizar
                      </button>
                    </CxPcs2SettingRow>
                  </div>
                </CxPcs2Section>
              </div>
            ) : (
              <div className="pcs2-page cx-exp-pcs2-page">
                <CxPcs2Section
                  stacked
                  icon={Sparkles}
                  title="Regras de avaliação"
                  status={(
                    <CxPcs2StatusChip
                      active={rules.length > 0}
                      label={rulesStatusLabel}
                    />
                  )}
                >
                  <div className="cx-exp-panel">
                    <div className="cx-exp-rules-toolbar cx-exp-rules-toolbar--actions">
                      <span className={`cx-exp-tooltip-wrap${hasDraftRule ? ' cx-exp-tooltip-wrap--disabled' : ''}`}>
                        <button
                          type="button"
                          className="cx-exp-add-rule-btn"
                          onClick={addRule}
                          disabled={hasDraftRule}
                        >
                          <Plus size={13} strokeWidth={2.5} aria-hidden />
                          Nova regra
                        </button>
                        {hasDraftRule ? (
                          <span className="cx-exp-tooltip" role="tooltip">
                            Salve a regra atual antes de adicionar outra
                          </span>
                        ) : null}
                      </span>
                    </div>

                    {!rules.length ? (
                      <p className="cx-exp-empty">Nenhuma regra cadastrada. Clique em Nova regra para começar.</p>
                    ) : (
                      <div className="cx-exp-rules">
                        {rules.map((rule) => (
                          <RuleCard
                            key={rule.localId}
                            rule={rule}
                            onUpdate={(patch) => updateRule(rule.localId, patch)}
                            onRequestRemove={requestRemoveRule}
                            onAddMotive={(label, requireDetail) => addMotive(rule.localId, label, requireDetail)}
                            onToggleMotiveDetail={(idx) => toggleMotiveDetail(rule.localId, idx)}
                            onRemoveMotive={(idx) => removeMotive(rule.localId, idx)}
                            onReorderMotive={(from, to) => reorderMotive(rule.localId, from, to)}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </CxPcs2Section>
              </div>
            )}
          </div>

          <div className="cx-exp-actions">
            <button type="button" className="cx-exp-btn cx-exp-btn--ghost" onClick={load} disabled={saving}>
              Recarregar
            </button>
            <button
              type="button"
              className="cx-exp-btn cx-exp-btn--primary"
              onClick={save}
              disabled={saving || !isDirty}
            >
              {saving ? <Loader2 size={14} className="cx-exp-spin" aria-hidden /> : null}
              Salvar configuração
            </button>
          </div>
        </div>
      </div>

      {ruleDeleteTarget ? (
        <div
          className="modal-overlay cx-exp-confirm-overlay"
          role="presentation"
          onClick={() => !deletingRule && setRuleDeleteTarget(null)}
        >
          <div
            className="confirm-modal cx-exp-confirm-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cx-exp-delete-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="confirm-header">
              <h3 id="cx-exp-delete-title">Excluir regra</h3>
            </div>
            <div className="confirm-content">
              <p>
                Excluir a regra &quot;{ruleDeleteTarget.label}&quot;? Os motivos vinculados também serão removidos.
              </p>
            </div>
            <div className="confirm-actions">
              <button
                type="button"
                className="btn-cancel"
                disabled={deletingRule}
                onClick={() => setRuleDeleteTarget(null)}
              >
                Cancelar
              </button>
              <button type="button" className="btn-confirm" disabled={deletingRule} onClick={confirmRemoveRule}>
                {deletingRule ? 'Excluindo…' : 'Excluir'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      <CxEmailTemplateModal
        open={emailTemplateOpen}
        initialTemplate={emailTemplate}
        onClose={() => setEmailTemplateOpen(false)}
        onSaved={(saved) => {
          setEmailTemplate(mergeCxEmailTemplate(saved));
          toastSuccess('Modelo de e-mail salvo.');
        }}
      />
      {Toaster}
    </div>
  );
}

function CxStarPicker({ label, value, onChange, minBound, maxBound, ariaLabel }) {
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [layout, setLayout] = useState(null);

  const items = useMemo(
    () =>
      STAR_OPTIONS.filter((n) => (minBound == null || n >= minBound) && (maxBound == null || n <= maxBound)).map(
        (n) => ({ value: n, label: `${n} ★` })
      ),
    [minBound, maxBound]
  );

  const updateLayout = useCallback(() => {
    const el = anchorRef.current;
    if (!el || !open) {
      setLayout(null);
      return;
    }
    const rect = el.getBoundingClientRect();
    setLayout(computeToolbarFilterPopoverLayout(rect, STAR_PICKER_WIDTH, STAR_PICKER_MAX_HEIGHT));
  }, [open]);

  useLayoutEffect(() => {
    updateLayout();
  }, [open, items.length, updateLayout]);

  useEffect(() => {
    if (!open) return undefined;
    const onResize = () => updateLayout();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
    };
  }, [open, updateLayout]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      const t = event.target;
      if (anchorRef.current?.contains(t)) return;
      if (t.closest?.('.board-search-filters-popover')) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  return (
    <>
      <div className="cx-exp-star-picker" ref={anchorRef}>
        {label ? <span className="cx-exp-star-picker-label">{label}</span> : null}
        <button
          type="button"
          className={`cx-exp-star-picker-trigger${value == null ? ' cx-exp-star-picker-trigger--empty' : ''}`}
          aria-label={ariaLabel || label || 'Selecionar estrelas'}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          <span>{value == null ? 'Selecionar' : `${value} ★`}</span>
          <ChevronDown size={11} strokeWidth={1.75} className={open ? 'is-open' : ''} aria-hidden />
        </button>
      </div>
      {open && layout
        ? createPortal(
            <div
              className="board-search-filters-popover board-search-filters-popover--simple cx-exp-star-picker-popover"
              style={{
                position: layout.position,
                top: layout.top,
                left: layout.left,
                width: layout.width,
                boxSizing: layout.boxSizing,
                zIndex: layout.zIndex,
                maxHeight: layout.maxHeight,
                '--bsf-cidade-popover-max-h': layout['--bsf-cidade-popover-max-h'],
                transform: layout.flipped ? 'translateY(-100%)' : undefined,
              }}
              role="dialog"
              aria-label={ariaLabel || label || 'Estrelas'}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <BoardSearchSimplePickerPanel
                showSearch={false}
                items={items}
                getKey={(i) => i.value}
                getLabel={(i) => i.label}
                selectedKeys={value == null ? [] : [value]}
                onToggle={(item) => {
                  onChange(item.value);
                  setOpen(false);
                }}
                emptyMessage="Sem opções"
                listAriaLabel={ariaLabel || label || 'Estrelas'}
              />
            </div>,
            document.body
          )
        : null}
    </>
  );
}

function RuleCard({ rule, onUpdate, onRequestRemove, onAddMotive, onRemoveMotive, onToggleMotiveDetail, onReorderMotive }) {
  const [motiveDraft, setMotiveDraft] = useState('');
  const [motiveRequireDetail, setMotiveRequireDetail] = useState(false);
  const [motiveInputOpen, setMotiveInputOpen] = useState(false);
  const [motiveDragIndex, setMotiveDragIndex] = useState(null);
  const [motiveDropIndex, setMotiveDropIndex] = useState(null);
  const motiveInputRef = useRef(null);

  useEffect(() => {
    if (!motiveInputOpen) return undefined;
    motiveInputRef.current?.focus();
    const onPointerDown = (event) => {
      if (event.target.closest?.('.cx-exp-motive-add-pop')) return;
      if (event.target.closest?.('.cx-exp-motive-fab')) return;
      setMotiveInputOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [motiveInputOpen]);

  const submitMotive = () => {
    onAddMotive(motiveDraft, motiveRequireDetail);
    setMotiveDraft('');
    setMotiveRequireDetail(false);
    setMotiveInputOpen(false);
  };

  const toggleAskReason = () => {
    const next = !rule.askReason;
    onUpdate({
      askReason: next,
      requireNoteOnMotive: next ? rule.requireNoteOnMotive : false,
    });
  };

  const handleMotiveDragStart = (e, index) => {
    setMotiveDragIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
  };

  const handleMotiveDragOver = (e, index) => {
    if (motiveDragIndex == null) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setMotiveDropIndex(index);
  };

  const handleMotiveDrop = (e, toIndex) => {
    e.preventDefault();
    const fromIndex = parseInt(e.dataTransfer.getData('text/plain'), 10);
    setMotiveDragIndex(null);
    setMotiveDropIndex(null);
    if (Number.isNaN(fromIndex) || fromIndex === toIndex) return;
    onReorderMotive(fromIndex, toIndex);
  };

  const handleMotiveDragEnd = () => {
    setMotiveDragIndex(null);
    setMotiveDropIndex(null);
  };

  return (
    <article className={`cx-exp-rule-card${rule.isDraft ? ' cx-exp-rule-card--draft' : ''}`}>
      <div className="cx-exp-rule-head">
        <div className="cx-exp-rule-head-left">
          <StarRow min={rule.starsMin} max={rule.starsMax} />
          <span
            className={`cx-exp-rule-range${rule.starsMin == null || rule.starsMax == null ? ' cx-exp-rule-range--pending' : ''}`}
          >
            {starsLabel(rule.starsMin, rule.starsMax)}
          </span>
        </div>
        <button
          type="button"
          className="cx-exp-rule-remove"
          onClick={() => onRequestRemove(rule)}
          aria-label="Excluir regra"
        >
          <Trash2 size={13} strokeWidth={2} aria-hidden />
        </button>
      </div>

      <div className="cx-exp-rule-grid">
        <CxStarPicker
          label="De"
          value={rule.starsMin}
          maxBound={rule.starsMax}
          ariaLabel="Estrelas mínimas"
          onChange={(v) =>
            onUpdate({
              starsMin: v,
              starsMax: rule.starsMax == null ? null : Math.max(v, rule.starsMax),
            })
          }
        />
        <CxStarPicker
          label="Até"
          value={rule.starsMax}
          minBound={rule.starsMin}
          ariaLabel="Estrelas máximas"
          onChange={(v) =>
            onUpdate({
              starsMax: v,
              starsMin: rule.starsMin == null ? null : Math.min(v, rule.starsMin),
            })
          }
        />
        <div className="cx-exp-rule-toggles">
          <button
            type="button"
            aria-pressed={rule.askReason}
            className={`user-form-toggle-pill cx-exp-rule-toggle${rule.askReason ? ' active' : ''}`}
            onClick={toggleAskReason}
          >
            <BadgeCheck size={12} strokeWidth={2} aria-hidden />
            <span>Solicitar motivo</span>
          </button>
          <button
            type="button"
            aria-pressed={rule.requireNote}
            className={`user-form-toggle-pill cx-exp-rule-toggle${rule.requireNote ? ' active' : ''}`}
            onClick={() => onUpdate({ requireNote: !rule.requireNote })}
          >
            <BadgeCheck size={12} strokeWidth={2} aria-hidden />
            <span>Observação geral</span>
          </button>
          <button
            type="button"
            aria-pressed={rule.requireNoteOnMotive}
            disabled={!rule.askReason}
            className={`user-form-toggle-pill cx-exp-rule-toggle${rule.requireNoteOnMotive ? ' active' : ''}${!rule.askReason ? ' cx-exp-rule-toggle--disabled' : ''}`}
            onClick={() => rule.askReason && onUpdate({ requireNoteOnMotive: !rule.requireNoteOnMotive })}
          >
            <BadgeCheck size={12} strokeWidth={2} aria-hidden />
            <span>Observação ao motivo</span>
          </button>
        </div>
      </div>

      {rule.askReason ? (
        <div className="cx-exp-motives">
          <span className="cx-exp-motives-label">Motivos cadastráveis</span>
          <div className="cx-exp-motives-list">
            {rule.motives.map((m, idx) => (
              <span
                key={m.id ?? `${m.label}-${idx}`}
                className={`cx-exp-motive-chip${m.requireDetail ? ' cx-exp-motive-chip--detail' : ''}${motiveDragIndex === idx ? ' cx-exp-motive-chip--dragging' : ''}${motiveDropIndex === idx ? ' cx-exp-motive-chip--drop-target' : ''}`}
                onDragOver={(e) => handleMotiveDragOver(e, idx)}
                onDragLeave={() => setMotiveDropIndex(null)}
                onDrop={(e) => handleMotiveDrop(e, idx)}
              >
                <span
                  className="cx-exp-motive-drag-handle"
                  draggable
                  role="button"
                  tabIndex={0}
                  aria-label={`Reordenar ${m.label}`}
                  onDragStart={(e) => handleMotiveDragStart(e, idx)}
                  onDragEnd={handleMotiveDragEnd}
                >
                  <GripVertical size={11} strokeWidth={2} aria-hidden />
                </span>
                {m.label}
                <button
                  type="button"
                  className={`cx-exp-motive-detail-btn${m.requireDetail ? ' active' : ''}`}
                  aria-pressed={m.requireDetail}
                  aria-label={`${m.requireDetail ? 'Desativar' : 'Ativar'} exigência de detalhe para ${m.label}`}
                  title={m.requireDetail ? 'Detalhe obrigatório' : 'Exigir detalhe ao selecionar'}
                  onClick={() => onToggleMotiveDetail(idx)}
                >
                  <MessageSquareText size={11} strokeWidth={2} aria-hidden />
                </button>
                <button type="button" onClick={() => onRemoveMotive(idx)} aria-label={`Remover ${m.label}`}>
                  ×
                </button>
              </span>
            ))}
            <CxExpAnchorTooltip text="Adicionar motivo" align="start">
              <button
                type="button"
                className="cx-exp-motive-fab"
                aria-label="Adicionar motivo"
                onClick={() => setMotiveInputOpen(true)}
              >
                <Plus size={14} strokeWidth={2.5} aria-hidden />
              </button>
            </CxExpAnchorTooltip>
            {motiveInputOpen ? (
              <div className="cx-exp-motive-add-pop">
                <input
                  ref={motiveInputRef}
                  type="text"
                  maxLength={120}
                  placeholder="Novo motivo…"
                  value={motiveDraft}
                  onChange={(e) => setMotiveDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      submitMotive();
                    }
                    if (e.key === 'Escape') {
                      e.preventDefault();
                      setMotiveDraft('');
                      setMotiveInputOpen(false);
                    }
                  }}
                />
                <button type="button" onClick={submitMotive} disabled={!motiveDraft.trim()}>
                  OK
                </button>
                <label className="cx-exp-motive-detail-check">
                  <input
                    type="checkbox"
                    checked={motiveRequireDetail}
                    onChange={(e) => setMotiveRequireDetail(e.target.checked)}
                  />
                  <span>Exigir detalhe</span>
                </label>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </article>
  );
}
